import { IconBell, IconLock, IconMail } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import type { MediumState } from '../../lib/notification-tiers';
import { Switch } from '../Switch';

// One medium (Email or Browser) for a tier or for one topic: its caption, then
// a switch, or a lock with its reason, or nothing.
//
// A locked cell is a lock icon and a visible sentence. Not a disabled switch
// (it reads as broken) and not a tooltip (a touch or keyboard user never sees
// one). The caption is `aria-hidden` because the switch's own name carries the
// medium and what it is for ("Email for Updates").
//
// The mixed count ("2 of 3 on") is a visible sibling, not an
// `aria-describedby` id: the shared `Switch` takes a name and no described-by,
// and the count is plain reading order beside the control it qualifies.

export function NotificationMediumControl(props: {
  readonly medium: 'email' | 'push';
  readonly state: MediumState;
  /** What the switch is for: a tier title or a topic label. */
  readonly name: string;
  readonly onChange: (next: boolean) => void;
}): JSX.Element {
  const isEmail = (): boolean => props.medium === 'email';
  const label = (): string =>
    t(
      isEmail()
        ? 'notifications_settings.tier.email_for'
        : 'notifications_settings.tier.browser_for',
      { name: props.name },
    );
  const caption = (): string =>
    t(isEmail() ? 'notifications_settings.tier.email' : 'notifications_settings.tier.browser');
  const switching = () => (props.state.kind === 'switch' ? props.state : undefined);

  return (
    <Show when={props.state.kind !== 'none'}>
      <div class="notification-medium">
        <span class="notification-medium__caption" aria-hidden="true">
          <Show when={isEmail()} fallback={<IconBell size="0.875rem" />}>
            <IconMail size="0.875rem" />
          </Show>
          {caption()}
        </span>
        <Show
          when={switching()}
          fallback={
            <p class="notification-medium__locked">
              <IconLock size="0.875rem" />
              <span>{t('notifications_settings.tier.locked_reason')}</span>
            </p>
          }
        >
          {(state) => (
            <span class="notification-medium__control">
              <Switch
                checked={state().checked}
                aria-label={label()}
                onActivate={() => props.onChange(!state().checked)}
              />
              <Show when={state().mixed}>
                <span class="notification-medium__mixed">
                  {t('notifications_settings.tier.mixed', {
                    on: state().on,
                    total: state().total,
                  })}
                </span>
              </Show>
            </span>
          )}
        </Show>
      </div>
    </Show>
  );
}
